import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

type ParamDef<T> = {
  default: T;
  parse?: (raw: string) => T;
  serialize?: (val: T) => string;
};

type ParamDefs = Record<string, ParamDef<any>>;

type InferValue<D> = D extends ParamDef<infer T> ? T : never;

type SearchParamsResult<D extends ParamDefs> = {
  [K in keyof D]: [InferValue<D[K]>, (val: InferValue<D[K]>) => void];
};

function parseValue<T>(raw: string, def: ParamDef<T>): T {
  if (def.parse) return def.parse(raw);
  const d = def.default;
  if (typeof d === 'boolean') return (raw === 'true') as T;
  if (typeof d === 'number') return Number(raw) as T;
  if (Array.isArray(d)) return (raw ? raw.split(',') : []) as T;
  return raw as T;
}

function serializeValue<T>(val: T, def: ParamDef<T>): string {
  if (def.serialize) return def.serialize(val);
  if (Array.isArray(val)) return val.join(',');
  return String(val);
}

/**
 * Syncs component state with URL query params. Each param definition specifies a default value
 * and optional parse/serialize functions. Types are auto-detected from defaults (string, boolean,
 * number, string[]). Params at their default value are omitted from the URL.
 *
 * Returns `{ [key]: [value, setter] }` — same API shape as `useState`.
 *
 * Consecutive setters in the same event (e.g. setEnvironment + setView) merge correctly.
 * react-router's `setSearchParams` applies functional updaters against the render-time
 * `searchParams` rather than chaining them, so without a shared ref the second setter
 * would overwrite the first.
 */
export function useSearchParamsState<D extends ParamDefs>(defs: D): SearchParamsResult<D> {
  const [searchParams, setSearchParams] = useSearchParams();
  const latestParamsRef = useRef(searchParams);
  latestParamsRef.current = searchParams;

  const result = {} as SearchParamsResult<D>;

  for (const key of Object.keys(defs)) {
    const def = defs[key];
    const raw = searchParams.get(key);
    const value = raw != null ? parseValue(raw, def) : def.default;

    const setter = (val: any) => {
      const next = new URLSearchParams(latestParamsRef.current);
      const serialized = serializeValue(val, def);
      const defaultSerialized = serializeValue(def.default, def);
      if (serialized === defaultSerialized) {
        next.delete(key);
      } else {
        next.set(key, serialized);
      }
      // Publish before navigate so a second setter in the same tick merges on top.
      latestParamsRef.current = next;
      setSearchParams(next, { replace: true });
    };

    (result as any)[key] = [value, setter];
  }

  return result;
}
